import { apiCandidates } from "@/api/candidate.api.ts";
import {
  useModals,
  Button,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Tag,
  TagVariants,
  Text,
  Title,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CandidateAIScoreNA } from "@/pages/recruit/candidates/components/CandidateAIScoreNA.tsx";
import { CandidateAIScoreEditModal } from "@/pages/recruit/candidates/widgets/CandidateAIScoreEditModal.tsx";
import { useCandidateUpdateAIScore } from "@/queries/recruit/use-candidates.query.ts";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import {
  ApiCandidate,
  getCandidateScoreCircleEmoji,
  getCandidateScoreMarkTitle,
  getCandidateScoreMarkVariant,
} from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function CandidateAIScore({ candidate }: { candidate: ApiCandidate }) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { canIEdit } = usePermissions();
  const { openModal } = useModals();
  const [showDetails, setShowDetails] = useState(false);
  const [isCalculationRequested, setIsCalculationRequested] = useState(false);
  const [isCalculateLoading, setIsCalculateLoading] = useState(false);

  const candidateAIScoreUpdate = useCandidateUpdateAIScore();

  const handleCalculate = async () => {
    setIsCalculateLoading(true);
    try {
      await apiCandidates.evaluateScore(candidate.id);
      setIsCalculateLoading(false);
      setIsCalculationRequested(true);
    } catch {
      setIsCalculateLoading(false);
    }
  };

  const getTagVariant = (): TagVariants => {
    if (!candidate.ai_score) return "primary";

    return getCandidateScoreMarkVariant(candidate.ai_score.ai_score_mark);
  };

  const renderCalculating = () => {
    return (
      <Stack
        gap={"md"}
        justify={"between"}
        direction={"horizontal"}
        items={"center"}
        className={"py-4 border-b border-dark/10"}
      >
        <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
          <Tag size={"xl"} appearance={"filled"} square={true} glow={true} variant={"primary"}>
            <FontAwesomeIcon icon={"fa-light fa-timer font-semibold"} />
          </Tag>
          <Title
            header={t("ai:calculating_match")}
            caption={t("ai:calculating_match_description")}
          />
        </Stack>
      </Stack>
    );
  };

  const handleEditScore = () => {
    openModal(CandidateAIScoreEditModal, {
      value: candidate.ai_score?.ai_score_value ?? 0,
      onApply: async function (value) {
        try {
          await candidateAIScoreUpdate.mutateAsync({
            id: candidate.id,
            score: value,
          });
        } catch {
          return;
        }
      },
    });
  };

  if (candidate.ai_score_status == ApiAIProcessingStatus.disabled) {
    return null;
  }

  if (isCalculationRequested) {
    return renderCalculating();
  }

  if (
    [ApiAIProcessingStatus.waiting, ApiAIProcessingStatus.failed].includes(
      candidate.ai_score_status
    )
  ) {
    return (
      <CandidateAIScoreNA
        candidate={candidate}
        handleCalculate={handleCalculate}
        isCalculateLoading={isCalculateLoading}
      />
    );
  }

  if (!candidate.ai_score) {
    return renderCalculating();
  }

  return (
    <Stack className={"cursor-pointer py-4 border-b border-dark/10"} gap={"md"}>
      <Stack
        gap={"md"}
        justify={"between"}
        direction={"horizontal"}
        items={"center"}
        onClick={() => setShowDetails(!showDetails)}
      >
        <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
          <Tag
            size={"xl"}
            appearance={"filled"}
            square={true}
            glow={true}
            variant={getTagVariant()}
          >
            {candidate.ai_score.ai_score_value}
          </Tag>
          <Stack>
            <Stack direction={"horizontal"} gap={"xs"}>
              <Text className={"font-semibold text-title"}>
                {getCandidateScoreMarkTitle(candidate.ai_score.ai_score_mark)}
              </Text>
              <Text className={"text-secondary"}>{t("ai:match_title")}</Text>
            </Stack>

            {!candidate.ai_score.ai_score_edited_by && (
              <Text className={"text-secondary"}>{t("ai:based_on_ai")}</Text>
            )}
            {candidate.ai_score.ai_score_edited_by && (
              <Text
                className={"text-secondary"}
              >{`${t("common:edited")} ${renderEmployeeName(candidate.ai_score.ai_score_edited_by)}`}</Text>
            )}
          </Stack>
        </Stack>
        <FontAwesomeIcon
          icon={"fa-light " + (showDetails ? "fa-chevron-down" : "fa-chevron-right")}
        />
      </Stack>
      {showDetails && (
        <Stack>
          <ListItem preTitle={t("common:summary")} title={candidate.ai_score.ai_score_summary} />
          {candidate.ai_score.ai_score_skills_evaluation.map((skill) => (
            <ListItem
              emoji={getCandidateScoreCircleEmoji(skill.mark)}
              key={skill.title}
              preTitle={skill.title}
              title={getCandidateScoreMarkTitle(skill.mark)}
            />
          ))}
          {canIEdit(ApiPermission.manage_candidates) && (
            <Stack className={"mt-4"} gap={"sm"} direction={"horizontal"}>
              <Button
                size={"sm"}
                variant={"secondary"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditScore();
                }}
              >
                {t("ai:edit_score")}
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCalculate();
                }}
                variant={"secondary"}
                size={"sm"}
                isLoading={isCalculateLoading}
              >
                {t("ai:recalculate")}
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
